import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  Check,
  ChevronsUpDown,
  Download,
  FileSpreadsheet,
  FileText,
  FileUp,
  Loader2,
  Package,
  Pencil,
  Search,
  Upload,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { mesEnLetras, tramoCorto } from "@/lib/periodo";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/carga")({ component: Carga });

// ===========================================================================
// Cargar los estados de cuenta del mes.
//
// La pantalla que faltaba: la ingesta llevaba construida y probada con 305
// archivos, y su único llamador era un script de terminal. Contabilidad no va a
// usar una terminal.
//
// El orden importa y es el que pidieron: se sube, se ENSEÑA lo que se entendió,
// se corrige lo que no cuadre, y recién entonces entra a la base. Hasta ahora
// era un solo acto, así que un extracto mal detectado ya estaba dentro cuando
// alguien se daba cuenta.
// ===========================================================================

export const BANCOS = ["BCP", "BBVA", "INTERBANK", "ALFIN"] as const;

/** Lo que devuelve `/carga` por cada archivo en revisión. */
/**
 * Una cuenta del maestro, como se elige en el selector.
 *
 * Va desarmada en campos y no como una etiqueta ya montada porque el selector
 * busca por cada uno de ellos —número, banco, razón social, RUC— y ordena
 * poniendo delante las del banco y la moneda que se detectaron. Con 154 cuentas
 * en el maestro, una lista sin buscador ni orden es una lista que no se usa.
 */
export type CuentaElegible = {
  id: string;
  banco: string;
  numero: string;
  moneda: "PEN" | "USD";
  razonSocial: string;
  ruc: string;
};

/**
 * EL FORMULARIO DE ANTES DE SUBIR.
 *
 * Pregunta dos cosas y no deja seguir sin la razón social, que es la que no se
 * puede deducir del papel con seguridad. El mes viene puesto en el periodo
 * abierto, que es el caso normal, pero se puede cambiar: un expediente que
 * llega tarde pertenece al mes en que se emitió, no al mes en que alguien lo
 * archivó.
 */
function FormularioDeCarga({
  archivos,
  clase,
  empresas,
  periodos,
  empresaId,
  periodoId,
  alCambiarEmpresa,
  alCambiarPeriodo,
  alCrearPeriodo,
  creandoPeriodo,
  alCancelar,
  alConfirmar,
}: {
  archivos: File[];
  clase: "estado_cuenta" | "comprobante";
  empresas: Array<{
    id: string;
    ruc: string;
    razonSocial: string;
    cuentas: number;
  }>;
  periodos: Array<{ id: string; nombre: string }>;
  empresaId: string;
  periodoId: string;
  alCambiarEmpresa: (v: string) => void;
  alCambiarPeriodo: (v: string) => void;
  alCrearPeriodo: (nombre: string) => void;
  creandoPeriodo: boolean;
  alCancelar: () => void;
  alConfirmar: () => void;
}) {
  // BUSCAR LA RAZÓN SOCIAL ESCRIBIENDO.
  //
  // Con ocho empresas un desplegable ya obliga a leerlas todas; con las del
  // grupo entero, a buscar «MERCADO XV» entre «MERCADO X», «XI», «XII»… que se
  // parecen entre sí en todo menos en el número. Se filtra por razón social y
  // por RUC, porque a veces lo que se tiene a mano es el RUC.
  const [texto, setTexto] = useState("");
  const elegida = empresas.find((e) => e.id === empresaId) ?? null;
  const t = texto.trim().toLowerCase();
  const salen = t
    ? empresas.filter(
        (e) => e.razonSocial.toLowerCase().includes(t) || e.ruc.includes(t),
      )
    : empresas;

  // ABRIR UN MES QUE NO ESTÁ.
  //
  // Un extracto de febrero llega antes de que nadie haya abierto febrero, y
  // hasta ahora eso era un callejón: el mes solo nacía desde la terminal. Se
  // propone crearlo desde aquí, sin salir de la carga.
  const [mesNuevo, setMesNuevo] = useState("");
  const yaEsta = periodos.some((p) => p.nombre === mesNuevo);
  const mesValido = /^\d{4}-(0[1-9]|1[0-2])$/.test(mesNuevo);

  const listo = Boolean(empresaId && periodoId);
  const que =
    clase === "comprobante" ? "expedientes de clientes" : "estados de cuenta";

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border bg-background p-5 shadow-lg">
        <h2 className="font-semibold text-[15px]">
          ¿De quién y de qué mes son estos {que}?
        </h2>
        <p className="mt-1 text-[12.5px] text-muted-foreground">
          {archivos.length}{" "}
          {archivos.length === 1 ? "archivo listo" : "archivos listos"} para
          subir. Hace falta saberlo antes: cada razón social tiene sus propias
          cuentas de banco, y de ahí sale después si un abono es un préstamo
          entre razones sociales del grupo.
        </p>

        <div className="mt-4 space-y-4">
          <div>
            <span className="font-medium text-[12.5px]">Razón social</span>
            {elegida ? (
              <div className="mt-1 flex items-center gap-2 rounded border bg-muted/40 px-2 py-1.5">
                <span className="min-w-0 flex-1 truncate text-[12.5px]">
                  <b>{elegida.razonSocial}</b>{" "}
                  <span className="text-muted-foreground">
                    · {elegida.ruc} · {elegida.cuentas} cuentas
                  </span>
                </span>
                <button
                  type="button"
                  className="shrink-0 text-[11.5px] text-muted-foreground underline"
                  onClick={() => {
                    alCambiarEmpresa("");
                    setTexto("");
                  }}
                >
                  cambiar
                </button>
              </div>
            ) : (
              <>
                <input
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  placeholder="Escribí parte del nombre o el RUC…"
                  className="mt-1 w-full rounded border bg-background px-2 py-1.5 text-[12.5px]"
                />
                <div className="mt-1 max-h-44 overflow-y-auto rounded border">
                  {salen.length === 0 ? (
                    <p className="px-2 py-3 text-center text-[11.5px] text-muted-foreground">
                      Ninguna razón social coincide con «{texto}».
                    </p>
                  ) : (
                    salen.map((e) => (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => alCambiarEmpresa(e.id)}
                        className="block w-full border-b px-2 py-1.5 text-left text-[12.5px] last:border-0 hover:bg-muted/60"
                      >
                        <b>{e.razonSocial}</b>{" "}
                        <span className="text-[11px] text-muted-foreground">
                          · {e.ruc} · {e.cuentas} cuentas
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          <div>
            <span className="font-medium text-[12.5px]">Mes y año</span>
            <select
              className="mt-1 w-full rounded border bg-background px-2 py-1.5 text-[12.5px]"
              value={periodoId}
              onChange={(e) => alCambiarPeriodo(e.target.value)}
            >
              {periodos.length === 0 ? (
                <option value="">— todavía no hay ningún mes abierto —</option>
              ) : null}
              {periodos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>

            {/* El mes que falta se abre desde aquí. Un extracto de febrero
                llega antes de que nadie haya abierto febrero. */}
            <div className="mt-1.5 flex items-center gap-2">
              <input
                value={mesNuevo}
                onChange={(e) => setMesNuevo(e.target.value)}
                placeholder="¿No está? AAAA-MM"
                className="w-32 rounded border bg-background px-2 py-1 text-[11.5px]"
              />
              <Button
                variant="outline"
                size="sm"
                disabled={!mesValido || yaEsta || creandoPeriodo}
                onClick={() => alCrearPeriodo(mesNuevo)}
              >
                {creandoPeriodo
                  ? "Abriendo…"
                  : yaEsta
                    ? "Ese mes ya está"
                    : "Abrir el mes"}
              </Button>
            </div>
          </div>
        </div>

        <p className="mt-3 text-[11.5px] text-muted-foreground">
          Si el papel dice otra cosa —otra empresa o otra fecha— el archivo
          entra igual y queda marcado, para que se pueda revisar.
        </p>

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={alCancelar}>
            Cancelar
          </Button>
          <Button size="sm" disabled={!listo} onClick={alConfirmar}>
            {listo
              ? `Subir ${archivos.length} ${archivos.length === 1 ? "archivo" : "archivos"}`
              : empresaId
                ? "Falta el mes"
                : "Falta la razón social"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Una empresa del maestro, con las cuentas que tiene. */
export type EmpresaMaestro = {
  ruc: string;
  razonSocial: string;
  cuentas: Array<{
    id: string;
    banco: string;
    numero: string;
    moneda: "PEN" | "USD";
  }>;
};

export type Borrador = {
  id: string;
  nombre: string;
  bytes: number;
  clase: "estado_cuenta" | "comprobante";
  banco: string | null;
  origenBanco: "texto" | "nombre" | "ninguno";
  moneda: "PEN" | "USD" | null;
  empresa: string | null;
  cuenta: string | null;
  cuentaId: string | null;
  porQueLaCuenta: string | null;
  desde: string | null;
  hasta: string | null;
  movimientos: number;
  cuadre: {
    declaradoAbonos: number | null;
    calculadoAbonos: number;
    declaradoCargos: number | null;
    calculadoCargos: number;
    cuadra: boolean;
  } | null;
  hallazgos: Array<{
    codigo: string;
    gravedad: "error" | "aviso";
    mensaje: string;
  }>;
  /** Las líneas del extracto, para poder corregirlas antes de confirmar. */
  filas: FilaExtracto[];
  /** Los índices de `filas` que ya tocó una persona. */
  filasTocadas: number[];
  listo: boolean;
  falta: string | null;
  error: string | null;
  tocadoAMano: boolean;
};

/** Una línea del extracto, tal como se lee y como se puede corregir. */
export type FilaExtracto = {
  linea: number;
  pagina: number;
  fechaOper: string;
  fechaValor: string | null;
  descripcion: string;
  referencia: string | null;
  canal: string | null;
  nroOperacion: string | null;
  abono: number | null;
  cargo: number | null;
  /** El impuesto que el banco cobra en la MISMA línea. Resta como el cargo. */
  itf: number | null;
  saldo: number | null;
};

/** Céntimos a texto con separadores. `null` es «—», que no es lo mismo que 0. */
const dinero = (c: number | null) =>
  c === null
    ? "—"
    : (c / 100).toLocaleString("es-PE", { minimumFractionDigits: 2 });

/** Una moneda del resumen del mes: cuántos movimientos, cuánto entró y salió. */
type PorMoneda = {
  moneda: "PEN" | "USD";
  movimientos: number;
  entro: number;
  salio: number;
};

/** Una cuenta del resumen, con el saldo con el que cierra su extracto. */
type PorCuenta = PorMoneda & {
  archivoId: string;
  banco: string;
  numero: string;
  empresa: string;
  saldoFinal: number | null;
  /** De cuándo son sus movimientos. Es lo que distingue dos extractos de la
      misma cuenta en meses distintos, que si no se ven idénticos. */
  desde: string | null;
  hasta: string | null;
};

function Carga() {
  const { data, isPending } = api.enRevision.useQuery(["carga"]);
  const d = data?.status === 200 ? data.body : null;
  // El maestro, para poder decir a mano a qué cuenta pertenece un extracto que
  // no se resolvió solo. Sin esto la pantalla sabía decir «no se sabe a qué
  // cuenta pertenece» y no daba forma de arreglarlo: un callejón sin salida
  // justo en el archivo que más ayuda necesita.
  const { data: maestros } = api.maestros.useQuery(["maestros-carga"], {
    query: {},
  });
  const cuentas: CuentaElegible[] =
    maestros?.status === 200
      ? (maestros.body.filas as EmpresaMaestro[]).flatMap((e) =>
          e.cuentas.map((c) => ({
            id: c.id,
            banco: c.banco,
            numero: c.numero,
            moneda: c.moneda,
            razonSocial: e.razonSocial,
            ruc: e.ruc,
          })),
        )
      : [];
  const qc = useQueryClient();

  const [subiendo, setSubiendo] = useState(false);
  /**
   * Cuánto va subido, de 0 a 100. `null` mientras no haya subida en curso.
   *
   * Existe porque un mes de extractos son 236 MB: sin esto la pantalla enseña
   * un spinner mudo durante minutos, que es indistinguible de una pantalla
   * colgada — y quien no distingue las dos cosas recarga a mitad de la subida.
   */
  const [avance, setAvance] = useState<number | null>(null);
  const [encima, setEncima] = useState(false);
  const entrada = useRef<HTMLInputElement>(null);

  /**
   * Qué se está subiendo: extractos del banco o expedientes de clientes.
   *
   * Son los dos caminos del mes y no se parecen en nada —32 extractos que hay
   * que revisar uno a uno contra 273 expedientes que van al pipeline— pero
   * entran por la misma puerta. Hasta ahora esta pantalla mandaba SIEMPRE
   * `clase=estado_cuenta`, así que soltar aquí un expediente lo hacía intentar
   * leerse como un estado de cuenta del banco.
   */
  const [clase, setClase] = useState<"estado_cuenta" | "comprobante">(
    "estado_cuenta",
  );
  /** El último lote subido, para poder ir a ver cómo va. */
  const [ultimoLote, setUltimoLote] = useState<string | null>(null);
  /**
   * Cuántas veces le llegó una hoja de Excel a cada borrador.
   *
   * Es lo que fuerza a rehacer su rejilla con las filas nuevas. Va por
   * borrador y no como un contador único porque un contador único remontaría
   * las cinco tarjetas del mes y se llevaría por delante lo que alguien
   * estuviera escribiendo en las otras cuatro.
   */
  const [versiones, setVersiones] = useState<Record<string, number>>({});

  /**
   * Los extractos que acaban de subirse, para abrirles la hoja solos.
   *
   * Es el paso que faltaba del recorrido: se subía el archivo y la tabla para
   * corregirlo quedaba plegada detrás de una línea que parecía un separador,
   * así que el paso «revisá y corregí» —que es para lo que existe esta
   * pantalla— no se veía. Ahora lo que acaba de entrar se abre.
   *
   * SOLO lo recién subido, no todo lo que haya en revisión: un mes son 32
   * extractos de 158 líneas, y abrir los 32 son treinta mil casillas de golpe.
   */
  const [recienSubidos, setRecienSubidos] = useState<Set<string>>(new Set());
  /** Los ids que ya estaban antes de la subida, para saber cuáles son nuevos. */
  const antesDeSubir = useRef<Set<string>>(new Set());
  /**
   * Los ids que hay en pantalla ahora mismo.
   *
   * En una referencia y no en las dependencias de `subir`: rehacer ese callback
   * cada vez que la consulta refresca no aporta nada, y leerlo desde una
   * versión vieja daría por RECIÉN SUBIDO algo que ya estaba —y le abriría la
   * hoja a un extracto que nadie tocó.
   */
  const navegar = useNavigate();
  const idsEnPantalla = useRef<string[]>([]);
  /** Hay una subida esperando a que la consulta traiga sus borradores. */
  const [abrirLoNuevo, setAbrirLoNuevo] = useState(false);

  // La subida termina antes de que la consulta traiga los borradores nuevos, así
  // que abrirlos no se puede hacer en el mismo acto: se marca la intención y se
  // resuelve cuando llegan.
  useEffect(() => {
    idsEnPantalla.current = (d?.borradores ?? []).map(
      (b: { id: string }) => b.id,
    );
  }, [d?.borradores]);

  useEffect(() => {
    if (!abrirLoNuevo) return;
    const nuevos = (d?.borradores ?? []).filter(
      (b: { id: string }) => !antesDeSubir.current.has(b.id),
    );
    if (nuevos.length === 0) return;
    setRecienSubidos(
      (antes) =>
        new Set([...antes, ...nuevos.map((b: { id: string }) => b.id)]),
    );
    setAbrirLoNuevo(false);

    // UN SOLO ESTADO DE CUENTA: se abre directo en su pantalla, con el PDF al
    // lado. Corregir una línea es compararla contra el papel, y hasta ahora el
    // papel solo se podía ver DESPUÉS de confirmar — es decir, cuando ya no se
    // puede corregir sin deshacer la carga entera.
    //
    // Con varios no se navega a ninguno: mandar a uno de doce esconde los once
    // que quedan, y quien sube el mes entero necesita ver la lista para saber
    // cuáles cuadraron y cuáles no.
    const soloUno =
      nuevos.length === 1 &&
      (nuevos[0] as { clase?: string }).clase === "estado_cuenta";
    if (soloUno) {
      navegar({
        to: "/borrador/$borradorId",
        params: { borradorId: (nuevos[0] as { id: string }).id },
      });
    }
  }, [abrirLoNuevo, d?.borradores, navegar]);

  const corregir = api.corregirCarga.useMutation();
  const confirmar = api.confirmarCarga.useMutation();
  const descartar = api.descartarCarga.useMutation();

  // ANTES DE SUBIR SE DECLARA DE QUÉ MES Y DE QUÉ RAZÓN SOCIAL ES.
  //
  // Los archivos elegidos NO se mandan todavía: quedan aquí esperando a que
  // alguien conteste el formulario. No es burocracia — cada razón social del
  // grupo tiene sus propias cuentas de banco, así que saber de quién es lo que
  // entra es lo que después permite reconocer un préstamo entre razones
  // sociales. Y lo declarado se contrasta con lo que dice el papel.
  const [porDeclarar, setPorDeclarar] = useState<File[] | null>(null);
  const abrirMes = api.crearPeriodo.useMutation();
  const [empresaId, setEmpresaId] = useState<string>("");
  const [periodoId, setPeriodoId] = useState<string>("");

  const subir = useCallback(
    async (archivos: FileList | File[]) => {
      // El `.zip` entra: se abre en el servidor y cada PDF de dentro sigue el
      // mismo camino que si se hubiera soltado suelto. Antes no estaba en el
      // filtro y no había mensaje, así que un ZIP se descartaba en silencio.
      const lista = [...archivos].filter((f) =>
        /\.(pdf|xlsx|zip)$/i.test(f.name),
      );
      if (lista.length === 0) {
        toast.error("Solo entran PDF, Excel y ZIP");
        return;
      }
      setSubiendo(true);
      setAvance(0);
      // Lo que ya estaba. Lo que salga después y no esté aquí es lo que acaba
      // de subirse, y es a eso a lo que hay que abrirle la hoja.
      antesDeSubir.current = new Set(idsEnPantalla.current);
      const cuerpo = new FormData();
      cuerpo.append("clase", clase);
      if (empresaId) cuerpo.append("empresaId", empresaId);
      if (periodoId) cuerpo.append("periodoId", periodoId);
      for (const f of lista) cuerpo.append("archivos", f);
      try {
        // XMLHttpRequest y no `fetch`, y es la única razón por la que se usa
        // algo tan viejo: `fetch` NO emite progreso de SUBIDA. Un mes de
        // extractos son 236 MB, y con `fetch` la pantalla enseñaba un spinner
        // mudo durante minutos — indistinguible de una pantalla colgada, que es
        // lo que hace que alguien recargue a mitad de una subida.
        const { recibidos, loteId, descartadas } = await new Promise<{
          recibidos: Array<{
            nombre: string;
            duplicado: boolean;
            error: string | null;
          }>;
          loteId: string;
          descartadas: Array<{ ruta: string; motivo: string }>;
        }>((resolver, rechazar) => {
          const pet = new XMLHttpRequest();
          pet.open("POST", "/subir");
          pet.withCredentials = true;
          pet.upload.onprogress = (e) => {
            // `lengthComputable` es falso cuando el navegador no sabe el total.
            // Entonces no se inventa un porcentaje: se deja el indeterminado.
            if (e.lengthComputable) setAvance((e.loaded / e.total) * 100);
          };
          // Subido del todo, pero el servidor aún está leyendo los PDFs. Se
          // deja al 100 y el texto cambia: el trabajo no ha terminado.
          pet.upload.onload = () => setAvance(100);
          pet.onload = () => {
            if (pet.status < 200 || pet.status >= 300)
              return rechazar(new Error(pet.responseText));
            try {
              resolver(JSON.parse(pet.responseText));
            } catch {
              rechazar(new Error("respuesta ilegible del servidor"));
            }
          };
          pet.onerror = () => rechazar(new Error("no se pudo conectar"));
          pet.onabort = () => rechazar(new Error("subida cancelada"));
          pet.send(cuerpo);
        });
        const repetidos = recibidos.filter((x) => x.duplicado).length;
        const fallados = recibidos.filter(
          (x) => x.error && !x.duplicado,
        ).length;
        toast.success(
          `${recibidos.length - repetidos - fallados} para revisar` +
            (repetidos ? ` · ${repetidos} ya estaban` : "") +
            (fallados ? ` · ${fallados} no se pudieron leer` : ""),
        );
        // Lo que el ZIP traía y no se pudo usar. Va aparte y con su motivo: un
        // ZIP del que entran 271 de 273 sin decir qué pasó con los otros dos es
        // el modo de fallo que esta pantalla existe para evitar.
        if (descartadas?.length) {
          toast.warning(
            `${descartadas.length} entrada(s) del ZIP quedaron fuera`,
            {
              description: descartadas
                .slice(0, 3)
                .map((d) => `${d.ruta}: ${d.motivo}`)
                .join(" · "),
              duration: 12_000,
            },
          );
        }
        if (clase === "comprobante" && loteId) setUltimoLote(loteId);
        // Un extracto subido va derecho a su hoja: subir y corregir son UN
        // recorrido, no dos pantallas. Los expedientes no, que no tienen filas
        // que editar y su revisión es la del lote.
        if (clase === "estado_cuenta") setAbrirLoNuevo(true);
        qc.invalidateQueries();
      } catch {
        toast.error("No se pudieron subir los archivos");
      } finally {
        setSubiendo(false);
        setAvance(null);
      }
    },
    [qc, clase, empresaId, periodoId],
  );

  const guardar = (id: string, cambio: Record<string, string | null>) =>
    corregir.mutate(
      { params: { id }, body: cambio },
      {
        onSuccess: () => qc.invalidateQueries(),
        onError: () => toast.error("No se pudo corregir"),
      },
    );

  return (
    <>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
          Documentos <span className="text-border">/</span>
          <b className="font-semibold text-[19px] text-foreground tracking-tight">
            Cargar el mes
          </b>
        </div>
        {d ? (
          <span className="rounded-md bg-muted px-2.5 py-1 text-[12px]">
            Periodo {d.periodo}
          </span>
        ) : null}

        {/* Qué se está subiendo. Son los dos caminos del mes y no se parecen:
            32 extractos que se revisan uno a uno contra 273 expedientes que van
            al pipeline. Entraban por la misma puerta y todo se mandaba como
            estado de cuenta, así que un expediente soltado aquí intentaba
            leerse como el extracto de un banco. */}
      </div>

      {/* QUÉ SE ESTÁ SUBIENDO, dicho en grande y no en un conmutador de esquina.
          Son los dos caminos del mes y no se parecen: 32 extractos que se
          revisan uno a uno contra 273 expedientes que van al pipeline. El
          conmutador de antes eran dos palabras de 12 px arriba a la derecha, y
          soltar en el modo equivocado manda un expediente a leerse como si
          fuera el extracto de un banco. */}
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {(
          [
            [
              "estado_cuenta",
              "Estados de cuenta",
              "El PDF que manda el banco. Se revisa antes de entrar.",
            ],
            [
              "comprobante",
              "Expedientes de clientes",
              "La boleta con sus vouchers. Sueltos o en un ZIP.",
            ],
          ] as const
        ).map(([v, titulo, dice]) => (
          <button
            key={v}
            type="button"
            onClick={() => setClase(v)}
            aria-pressed={clase === v}
            className={cn(
              "flex items-start gap-3 rounded-lg border-2 p-3 text-left transition-colors",
              clase === v
                ? "border-cyan-500 bg-cyan-500/5"
                : "border-transparent bg-muted/50 hover:bg-muted",
            )}
          >
            <span
              className={cn(
                "mt-0.5 grid size-8 shrink-0 place-items-center rounded-md",
                clase === v
                  ? "bg-cyan-500 text-white"
                  : "bg-background text-muted-foreground",
              )}
            >
              {v === "estado_cuenta" ? (
                <FileSpreadsheet className="size-4" />
              ) : (
                <FileText className="size-4" />
              )}
            </span>
            <span className="min-w-0">
              <span className="block font-semibold text-[13px]">{titulo}</span>
              <span className="block text-[11.5px] text-muted-foreground">
                {dice}
              </span>
            </span>
          </button>
        ))}
      </div>

      {ultimoLote ? (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-cyan-500/40 bg-cyan-500/5 px-3 py-2.5 text-[12.5px]">
          <Package className="size-4 shrink-0 text-cyan-600" />
          <span>
            Los expedientes se están leyendo en segundo plano. Podés cerrar esto
            y volver: la revisión de completitud te espera ahí.
          </span>
          <Button size="sm" className="ml-auto" asChild>
            <Link to="/lote/$loteId" params={{ loteId: ultimoLote }}>
              Ver la revisión del lote
            </Link>
          </Button>
        </div>
      ) : null}

      {/* Soltar la carpeta. Un mes son 32 extractos o 273 expedientes: elegir
          de a uno en un diálogo no es una opción. */}
      {/* DE QUÉ MES Y DE QUÉ RAZÓN SOCIAL ES LO QUE SE SUBE.
          Se pregunta antes de mandar nada, porque de esto salen dos cosas que
          después no se pueden deducir: si un abono es un préstamo entre
          razones sociales del grupo —cada una tiene sus propias cuentas— y si
          el papel contradice lo que se declaró. */}
      {porDeclarar ? (
        <FormularioDeCarga
          archivos={porDeclarar}
          clase={clase}
          empresas={d?.empresas ?? []}
          periodos={d?.periodos ?? []}
          empresaId={empresaId}
          periodoId={periodoId || (d?.periodos ?? [])[0]?.id || ""}
          alCambiarEmpresa={setEmpresaId}
          alCambiarPeriodo={setPeriodoId}
          creandoPeriodo={abrirMes.isPending}
          alCrearPeriodo={(nombre) =>
            abrirMes.mutate(
              { body: { nombre } },
              {
                onSuccess: (r: {
                  status: number;
                  body: { id?: string; nombre?: string; yaEstaba?: boolean };
                }) => {
                  if (r.status !== 200) {
                    toast.error("No se pudo abrir el mes");
                    return;
                  }
                  // Se deja elegido el mes recién abierto: quien lo crea es
                  // porque va a cargarle algo ahora mismo.
                  if (r.body.id) setPeriodoId(r.body.id);
                  toast.success(
                    r.body.yaEstaba
                      ? `El mes ${r.body.nombre} ya estaba abierto`
                      : `Mes ${r.body.nombre} abierto`,
                  );
                  void qc.invalidateQueries();
                },
                onError: () => toast.error("No se pudo abrir el mes"),
              },
            )
          }
          alCancelar={() => setPorDeclarar(null)}
          alConfirmar={() => {
            const lista = porDeclarar;
            setPorDeclarar(null);
            void subir(lista);
          }}
        />
      ) : null}

      <button
        type="button"
        onDragOver={(e) => {
          e.preventDefault();
          setEncima(true);
        }}
        onDragLeave={() => setEncima(false)}
        onDrop={(e) => {
          e.preventDefault();
          setEncima(false);
          setPorDeclarar([...e.dataTransfer.files]);
        }}
        onClick={() => entrada.current?.click()}
        className={cn(
          "mt-4 flex w-full flex-col items-center gap-2 rounded-lg border-2 border-dashed p-10 transition-colors",
          encima ? "border-cyan-500 bg-cyan-500/5" : "hover:bg-muted/40",
        )}
      >
        {subiendo ? (
          <Loader2 className="size-7 animate-spin text-cyan-600" />
        ) : (
          <Upload className="size-7 text-muted-foreground" />
        )}
        <span className="font-medium text-[14px]">
          {subiendo
            ? // Dos fases, y decirlo importa: mientras sube hay una barra que
              // se mueve; cuando termina de subir el servidor todavía está
              // abriendo los PDFs, y ahí ya no hay porcentaje que enseñar.
              avance !== null && avance < 100
              ? `Subiendo… ${Math.round(avance)}%`
              : "Leyendo lo que subiste…"
            : clase === "comprobante"
              ? "Soltá acá los expedientes, sueltos o en un ZIP"
              : "Soltá acá los estados de cuenta del mes"}
        </span>
        {subiendo && avance !== null ? (
          <div className="h-1.5 w-56 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-cyan-500 transition-[width] duration-150"
              style={{ width: `${avance}%` }}
            />
          </div>
        ) : null}
        <span className="text-[12px] text-muted-foreground">
          {clase === "comprobante"
            ? "PDF de cada expediente, o el ZIP de la carpeta entera. Se leen en segundo plano."
            : "PDF del banco. Nada entra a la base hasta que lo confirmes."}
        </span>
        {/* EN QUÉ MES ENTRA LO QUE SE SUELTE. La pantalla lo decía arriba a la
            derecha, en gris y como «Periodo 2026-01». Cargar el extracto de
            febrero en enero es de los errores que solo se ven al cerrar. */}
        {d ? (
          <span className="rounded-full border border-cyan-500/40 bg-cyan-500/10 px-3 py-1 font-medium text-[11.5px] text-cyan-700 dark:text-cyan-300">
            Entra en {mesEnLetras(d.periodo)}
          </span>
        ) : null}
        <input
          ref={entrada}
          type="file"
          multiple
          accept=".pdf,.xlsx,.zip"
          className="hidden"
          onChange={(e) => {
            if (e.target.files) setPorDeclarar([...e.target.files]);
            e.target.value = "";
          }}
        />
      </button>

      {/* -------------------------------------------------------------------
          El resumen del mes, que es el paso 2 del guion de contabilidad:
          «que el programa haga un cálculo de la suma y resta de movimientos
          para obtener el total del mes como un resumen de cantidad de
          movimientos, dinero entrado y salido».

          Va aquí y no en otra pantalla porque es donde se acaba de confirmar
          la carga: la pregunta «¿lo que subí coincide con lo que el sistema
          entendió?» se contesta con el PDF todavía abierto al lado.

          Las dos monedas van separadas y sin total del grupo. Sumarlas
          necesitaría el tipo de cambio del día, que en este punto del mes
          todavía no se ha traído.
      ------------------------------------------------------------------- */}
      {d && d.mes.extractos > 0 ? (
        <div className="mt-5 rounded-lg border bg-card p-4 shadow-sm">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="font-medium text-[13px]">
              Lo que lleva cargado {mesEnLetras(d.periodo)}
            </h2>
            <span className="text-[11.5px] text-muted-foreground tabular-nums">
              {d.mes.extractos} extracto{d.mes.extractos === 1 ? "" : "s"} ·{" "}
              {d.mes.movimientos.toLocaleString("es-PE")} movimientos
            </span>
          </div>

          <div className="mt-3 flex flex-wrap gap-3">
            {(d.mes.porMoneda as PorMoneda[]).map((m) => (
              <div
                key={m.moneda}
                className="min-w-[220px] flex-1 rounded border bg-muted/30 px-3 py-2.5"
              >
                <div className="font-semibold text-[10.5px] text-muted-foreground uppercase tracking-wider">
                  {m.moneda === "PEN" ? "Soles" : "Dólares"} ·{" "}
                  {m.movimientos.toLocaleString("es-PE")} movimientos
                </div>
                <div className="mt-1.5 flex gap-5 tabular-nums">
                  <span className="text-[12.5px]">
                    <span className="text-muted-foreground">Entró </span>
                    <b className="text-emerald-700 dark:text-emerald-500">
                      {dinero(m.entro)}
                    </b>
                  </span>
                  <span className="text-[12.5px]">
                    <span className="text-muted-foreground">Salió </span>
                    <b className="text-red-700 dark:text-red-500">
                      {dinero(m.salio)}
                    </b>
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b text-[10.5px] text-muted-foreground uppercase tracking-wider">
                  <th className="py-1.5 text-left font-semibold">Cuenta</th>
                  <th className="py-1.5 text-left font-semibold">Mes</th>
                  <th className="py-1.5 text-right font-semibold">Movs.</th>
                  <th className="py-1.5 text-right font-semibold">Entró</th>
                  <th className="py-1.5 text-right font-semibold">Salió</th>
                  <th className="py-1.5 text-right font-semibold">
                    Saldo final
                  </th>
                </tr>
              </thead>
              <tbody>
                {(d.mes.porCuenta as PorCuenta[]).map((c) => (
                  <tr key={c.archivoId} className="border-b last:border-0">
                    <td className="py-1.5">
                      <Link
                        to="/archivo/$id"
                        params={{ id: c.archivoId }}
                        className="hover:underline"
                      >
                        <b>{c.banco}</b>{" "}
                        <span className="text-muted-foreground tabular-nums">
                          {c.numero}
                        </span>{" "}
                        <span className="text-[11px] text-muted-foreground">
                          {c.moneda === "PEN" ? "S/" : "US$"}
                        </span>
                      </Link>
                    </td>
                    <td className="py-1.5">
                      {tramoCorto(c.desde, c.hasta) ? (
                        <span className="rounded border px-1.5 py-0.5 text-[11px] tabular-nums">
                          {tramoCorto(c.desde, c.hasta)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      {c.movimientos.toLocaleString("es-PE")}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      {dinero(c.entro)}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      {dinero(c.salio)}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      {dinero(c.saldoFinal)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            El saldo final es el que imprime la última línea de cada extracto:
            es lo que se compara de un vistazo contra el PDF del banco.
          </p>
        </div>
      ) : null}

      {isPending ? (
        <div className="mt-4 space-y-3">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      ) : !d || d.borradores.length === 0 ? (
        <p className="mt-8 text-center text-muted-foreground text-sm">
          No hay nada esperando revisión.
        </p>
      ) : (
        <div className="mt-4 space-y-3">
          {(d.borradores as Borrador[]).map((b) => (
            <div
              key={b.id}
              className={cn(
                "rounded-lg border border-l-[3px] bg-card p-4 shadow-sm",
                b.listo ? "border-l-emerald-600" : "border-l-amber-600",
              )}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className="truncate font-medium text-[13px]"
                  title={b.nombre}
                >
                  {b.nombre.replace(/^.*\//, "")}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {(b.bytes / 1024).toFixed(0)} KB
                </span>
                {b.tocadoAMano ? (
                  <span className="rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide">
                    corregido a mano
                  </span>
                ) : null}
                <div className="ml-auto flex items-center gap-2">
                  {/* Al papel, que es donde de verdad se corrige. Con una carga
                      de doce extractos no se navega solo a ninguno, así que
                      cada uno necesita su puerta. */}
                  {b.clase === "estado_cuenta" ? (
                    <Button size="sm" variant="outline" asChild>
                      <Link
                        to="/borrador/$borradorId"
                        params={{ borradorId: b.id }}
                      >
                        Revisar con el PDF
                      </Link>
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    disabled={!b.listo || confirmar.isPending}
                    onClick={() =>
                      confirmar.mutate(
                        { params: { id: b.id }, body: { confirmo: true } },
                        {
                          onSuccess: (r: { body: { dice?: string } }) => {
                            toast.success(r.body.dice ?? "Confirmado");
                            qc.invalidateQueries();
                          },
                          onError: () => toast.error("No se pudo confirmar"),
                        },
                      )
                    }
                  >
                    <Check className="mr-1.5 size-3.5" />
                    Confirmar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      descartar.mutate(
                        { params: { id: b.id }, body: { motivo: null } },
                        { onSuccess: () => qc.invalidateQueries() },
                      )
                    }
                  >
                    <X className="size-3.5" />
                  </Button>
                </div>
              </div>

              {/* Lo que falta, dicho en una sola frase. Una lista de cinco
                  problemas no se lee, se ignora. */}
              {b.falta ? (
                <p className="mt-2 flex items-center gap-1.5 rounded bg-amber-500/15 px-2 py-1 text-[12px] text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="size-3.5 shrink-0" />
                  {b.falta}
                </p>
              ) : null}

              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Campo titulo="Banco">
                  <select
                    className="w-full rounded border bg-background px-2 py-1 text-[12.5px]"
                    value={b.banco ?? ""}
                    onChange={(e) =>
                      guardar(b.id, { banco: e.target.value || null })
                    }
                  >
                    <option value="">— sin detectar —</option>
                    {BANCOS.map((x) => (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    ))}
                  </select>
                  {/* De dónde salió importa: si lo dijo el NOMBRE del archivo y
                      no su contenido, renombrarlo habría cambiado el parser. De
                      los 32 extractos reales, 22 se detectan así. */}
                  <span className="text-[11px] text-muted-foreground">
                    {b.origenBanco === "texto"
                      ? "lo dice el propio extracto"
                      : b.origenBanco === "nombre"
                        ? "sale del nombre del archivo — conviene mirarlo"
                        : "no se pudo detectar"}
                  </span>
                </Campo>

                <Campo titulo="Moneda">
                  <select
                    className="w-full rounded border bg-background px-2 py-1 text-[12.5px]"
                    value={b.moneda ?? ""}
                    onChange={(e) =>
                      guardar(b.id, { moneda: e.target.value || null })
                    }
                  >
                    <option value="">— sin detectar —</option>
                    <option value="PEN">Soles</option>
                    <option value="USD">Dólares</option>
                  </select>
                </Campo>

                <Campo titulo="Cuenta de destino">
                  {/* SIEMPRE editable, esté resuelta o no. Antes la resuelta se
                      enseñaba como texto y solo la que faltaba se podía elegir:
                      un extracto asignado a la cuenta equivocada —pasa, porque
                      la detección va por el número impreso en el PDF y varios
                      bancos repiten numeración— no tenía forma de arreglarse
                      desde aquí. Y la cuenta es lo que decide la razón social a
                      la que se imputa cada abono del mes. */}
                  <SelectorDeCuenta
                    cuentas={cuentas}
                    cuentaId={b.cuentaId}
                    numero={b.cuenta}
                    empresa={b.empresa}
                    banco={b.banco}
                    moneda={b.moneda}
                    alElegir={(id) => guardar(b.id, { cuentaId: id })}
                  />
                  {b.porQueLaCuenta ? (
                    <span className="block text-[11px] text-muted-foreground">
                      {b.porQueLaCuenta}
                    </span>
                  ) : null}
                </Campo>

                <Campo titulo="Qué trae">
                  <span className="block font-medium text-[12.5px] tabular-nums">
                    {b.movimientos.toLocaleString("es-PE")} movimientos
                  </span>
                  <span className="text-[11.5px] text-muted-foreground">
                    {b.desde && b.hasta
                      ? `del ${b.desde} al ${b.hasta}`
                      : "sin periodo declarado"}
                  </span>
                </Campo>
              </div>

              {/* El cuadre: lo que declara el banco contra lo que sale de sumar
                  las líneas. Es la comprobación que dice si se leyó entero. */}
              {b.cuadre ? (
                <div
                  className={cn(
                    "mt-3 rounded border px-3 py-2 text-[12px]",
                    b.cuadre.cuadra
                      ? "border-emerald-600/30 bg-emerald-600/5"
                      : "border-red-600/40 bg-red-600/5",
                  )}
                >
                  <div className="flex flex-wrap gap-x-6 gap-y-1 tabular-nums">
                    <span>
                      Entró: <b>{dinero(b.cuadre.calculadoAbonos)}</b>
                      {b.cuadre.declaradoAbonos !== null ? (
                        <span className="text-muted-foreground">
                          {" "}
                          · el banco declara {dinero(b.cuadre.declaradoAbonos)}
                        </span>
                      ) : null}
                    </span>
                    <span>
                      Salió: <b>{dinero(b.cuadre.calculadoCargos)}</b>
                      {b.cuadre.declaradoCargos !== null ? (
                        <span className="text-muted-foreground">
                          {" "}
                          · el banco declara {dinero(b.cuadre.declaradoCargos)}
                        </span>
                      ) : null}
                    </span>
                  </div>
                  {/* Qué se comprobó de verdad, que no siempre es lo mismo.
                      Antes salía «el banco declara —» y el recuadro en verde:
                      un guion mudo al lado de un visto bueno se lee como
                      «cuadra contra el banco», y de los cuatro extractos de
                      enero ninguno declara totales — Interbank y BBVA no los
                      imprimen en la cabecera. Lo que sí se comprobó en esos es
                      la cadena de saldos, que es una prueba distinta y hay que
                      nombrarla. */}
                  <p className="mt-1.5 text-[11.5px] text-muted-foreground">
                    {b.cuadre.declaradoAbonos !== null ||
                    b.cuadre.declaradoCargos !== null
                      ? b.cuadre.cuadra
                        ? "Cuadra con los totales que declara el banco."
                        : "NO cuadra con los totales que declara el banco."
                      : b.cuadre.cuadra
                        ? "Este banco no imprime totales en la cabecera. Lo comprobado es la cadena de saldos: cada línea sale de la anterior, así que no falta ninguna."
                        : "Este banco no imprime totales en la cabecera, y la cadena de saldos no cierra."}
                  </p>
                  {b.hallazgos.length > 0 ? (
                    <ul className="mt-1.5 space-y-0.5 text-[11.5px] text-muted-foreground">
                      {b.hallazgos.map((h) => (
                        <li key={h.codigo + h.mensaje}>
                          <b>{h.gravedad === "error" ? "Error" : "Aviso"}:</b>{" "}
                          {h.mensaje}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : b.error ? (
                <p className="mt-3 rounded border border-red-600/40 bg-red-600/5 px-3 py-2 text-[12px]">
                  {b.error}
                </p>
              ) : null}

              {b.clase === "estado_cuenta" && b.filas.length > 0 ? (
                <HojaDelExtracto
                  // La `key` lleva la versión para poder rehacer la rejilla
                  // desde cero cuando llega una hoja de Excel corregida: sin
                  // eso el componente se queda con las filas que tenía en el
                  // estado y la pantalla enseña lo de antes con el cuadre de
                  // lo de ahora. Va por borrador, para no tirar de paso lo que
                  // alguien esté escribiendo en otra tarjeta.
                  key={`${b.id}-${versiones[b.id] ?? 0}-${b.filas.length}`}
                  borradorId={b.id}
                  iniciales={b.filas as FilaExtracto[]}
                  yaTocadas={b.filasTocadas}
                  declaradoAbonos={b.cuadre?.declaradoAbonos ?? null}
                  declaradoCargos={b.cuadre?.declaradoCargos ?? null}
                  // Lo que se acaba de subir llega abierto: es el paso
                  // siguiente del recorrido, no algo que haya que ir a buscar.
                  abiertaDeEntrada={recienSubidos.has(b.id)}
                  alGuardar={() => qc.invalidateQueries()}
                  alLlegarHoja={() => {
                    setVersiones((v) => ({ ...v, [b.id]: (v[b.id] ?? 0) + 1 }));
                    qc.invalidateQueries();
                  }}
                />
              ) : null}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ===========================================================================
// A qué cuenta del maestro pertenece el extracto.
//
// Es el dato que convierte «un abono de 500» en «un abono de 500 a tal razón
// social», así que un extracto sin él no puede entrar — y uno con la cuenta
// equivocada mete el mes entero de una empresa en la contabilidad de otra.
//
// Se escribe para buscar en vez de desplegar 154 opciones. Con un `<select>`
// nativo hay que reconocer el número correcto a ojo entre otros que empiezan
// igual, y el desplegable tapa el extracto que se está revisando. Aquí se
// busca por número, banco, razón social o RUC, y las cuentas del banco y la
// moneda que se detectaron salen primero.
// ===========================================================================

const SIMBOLO = { PEN: "S/", USD: "US$" } as const;

/** `194-2345678-0-12` y `1942345678012` son el mismo número al buscar. */
const soloDigitos = (t: string) => t.replace(/\D/g, "");

export function SelectorDeCuenta({
  cuentas,
  cuentaId,
  numero,
  empresa,
  banco,
  moneda,
  alElegir,
}: {
  cuentas: CuentaElegible[];
  cuentaId: string | null;
  /** El número tal como lo devuelve el borrador, por si el maestro aún carga. */
  numero: string | null;
  empresa: string | null;
  banco: string | null;
  moneda: "PEN" | "USD" | null;
  alElegir: (id: string) => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const [busca, setBusca] = useState("");
  const [resaltada, setResaltada] = useState(0);
  const caja = useRef<HTMLDivElement>(null);

  const elegida = cuentas.find((c) => c.id === cuentaId) ?? null;

  // Cerrar al pulsar fuera. Un `onBlur` no basta: se puede pulsar en un hueco
  // de la tarjeta que no recibe foco y la lista se quedaría abierta encima del
  // extracto.
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: PointerEvent) => {
      if (!caja.current?.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener("pointerdown", fuera);
    return () => document.removeEventListener("pointerdown", fuera);
  }, [abierto]);

  const opciones = useMemo(() => {
    const t = busca.trim().toLowerCase();
    const d = soloDigitos(busca);
    const pasa = (c: CuentaElegible) => {
      if (!t) return true;
      if (c.razonSocial.toLowerCase().includes(t)) return true;
      if (c.banco.toLowerCase().includes(t)) return true;
      if (c.numero.toLowerCase().includes(t)) return true;
      // Los dígitos sueltos, que es como se busca de verdad: se lee «2345678»
      // en el PDF y en el maestro está escrito «194-2345678-0-12».
      return (
        d.length >= 3 &&
        (soloDigitos(c.numero).includes(d) || c.ruc.includes(d))
      );
    };
    // Las del banco y la moneda detectados delante: son las candidatas reales,
    // y dejarlas mezcladas entre 154 es obligar a leerlas todas.
    const puntos = (c: CuentaElegible) =>
      (banco && c.banco === banco ? 2 : 0) +
      (moneda && c.moneda === moneda ? 1 : 0);
    return cuentas
      .filter(pasa)
      .sort(
        (a, b) =>
          puntos(b) - puntos(a) ||
          a.razonSocial.localeCompare(b.razonSocial, "es") ||
          a.numero.localeCompare(b.numero, "es"),
      );
  }, [cuentas, busca, banco, moneda]);

  const visibles = opciones.slice(0, 60);

  const elegir = (c: CuentaElegible) => {
    alElegir(c.id);
    setAbierto(false);
    setBusca("");
  };

  // Lo que se enseña cerrado. Si el maestro todavía no llegó se cae al número
  // que trae el propio borrador: enseñar «elegí la cuenta» en un extracto que
  // sí la tiene resuelta es mentir durante el segundo que tarda la consulta.
  const rotulo = elegida
    ? `${elegida.banco} · ${elegida.numero} · ${SIMBOLO[elegida.moneda]}`
    : numero
      ? numero
      : null;
  const bajoRotulo = elegida?.razonSocial ?? empresa;

  // El aviso que importa: la cuenta elegida no es del banco o de la moneda que
  // dice el extracto. No lo impide —el maestro puede estar incompleto— pero
  // callarlo es dejar pasar el error que este selector existe para evitar.
  const discrepa =
    elegida &&
    ((banco && elegida.banco !== banco) ||
      (moneda && elegida.moneda !== moneda))
      ? `el extracto es ${banco ?? "de banco sin detectar"} ${moneda ? SIMBOLO[moneda] : ""} y esta cuenta es ${elegida.banco} ${SIMBOLO[elegida.moneda]}`.trim()
      : null;

  return (
    <div ref={caja} className="relative">
      <button
        type="button"
        onClick={() => {
          setAbierto((x) => !x);
          setResaltada(0);
        }}
        className={cn(
          "flex w-full items-center gap-2 rounded border bg-background px-2 py-1 text-left text-[12.5px] hover:bg-muted/50",
          !rotulo && "border-amber-600/60",
        )}
      >
        <span className="min-w-0 flex-1">
          {rotulo ? (
            <>
              <span className="block truncate font-medium tabular-nums">
                {rotulo}
              </span>
              {bajoRotulo ? (
                <span className="block truncate text-[11.5px] text-muted-foreground">
                  {bajoRotulo}
                </span>
              ) : null}
            </>
          ) : (
            <span className="text-amber-700 dark:text-amber-500">
              — elegí la cuenta —
            </span>
          )}
        </span>
        <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
      </button>

      {discrepa ? (
        <span className="mt-0.5 block text-[11px] text-amber-700 dark:text-amber-500">
          Ojo: {discrepa}.
        </span>
      ) : null}

      {abierto ? (
        <div className="absolute z-30 mt-1 w-[min(30rem,90vw)] overflow-hidden rounded-md border bg-popover shadow-lg">
          <div className="flex items-center gap-2 border-b px-2.5">
            <Search className="size-3.5 shrink-0 text-muted-foreground" />
            <input
              // biome-ignore lint/a11y/noAutofocus: la lista se abre para escribir en ella
              autoFocus
              value={busca}
              onChange={(e) => {
                setBusca(e.target.value);
                setResaltada(0);
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") return setAbierto(false);
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setResaltada((i) => Math.min(i + 1, visibles.length - 1));
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setResaltada((i) => Math.max(i - 1, 0));
                } else if (e.key === "Enter") {
                  e.preventDefault();
                  const c = visibles[resaltada];
                  if (c) elegir(c);
                }
              }}
              placeholder="Número de cuenta, banco, empresa o RUC"
              className="w-full bg-transparent py-2 text-[12.5px] focus:outline-none"
            />
          </div>

          <div className="max-h-72 overflow-y-auto py-1">
            {visibles.length === 0 ? (
              <p className="px-3 py-4 text-center text-[12px] text-muted-foreground">
                Ninguna cuenta del maestro coincide.
              </p>
            ) : (
              visibles.map((c, i) => (
                <button
                  key={c.id}
                  type="button"
                  onMouseEnter={() => setResaltada(i)}
                  onClick={() => elegir(c)}
                  className={cn(
                    "flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[12px]",
                    i === resaltada && "bg-muted",
                  )}
                >
                  <Check
                    className={cn(
                      "size-3.5 shrink-0",
                      c.id === cuentaId ? "text-emerald-600" : "opacity-0",
                    )}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">
                      <b className="tabular-nums">{c.numero}</b>{" "}
                      <span className="text-muted-foreground">
                        {c.banco} · {SIMBOLO[c.moneda]}
                      </span>
                    </span>
                    <span className="block truncate text-[11px] text-muted-foreground">
                      {c.razonSocial}
                    </span>
                  </span>
                </button>
              ))
            )}
            {opciones.length > visibles.length ? (
              <p className="px-3 py-1.5 text-[11px] text-muted-foreground">
                y {opciones.length - visibles.length} más — seguí escribiendo
                para acotar
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ===========================================================================
// La hoja de cálculo del extracto (paso 3 del guion, issue #43).
//
// «En caso algún dato no coincida, poder editarlo; aquí podemos ser creativos y
// manejarlo tipo Excel para que al usuario se le haga más familiar, y poder
// editar múltiples datos y subirlo todo en conjunto.»
//
// SE EDITA EL BORRADOR, NO LA BASE. Un movimiento que ya entró puede estar
// conciliado, y cambiarle el importe por detrás dejaría un cruce firmado contra
// una cifra que ya no existe. Aquí todavía no ha entrado nada.
//
// Lo que hace que se sienta un Excel y no un formulario: que se escriba directo
// en la celda, que las flechas y el tabulador muevan por la rejilla sin tocar el
// ratón, que se pegue un bloque desde el portapapeles y se copie otro de vuelta,
// y que el cuadre se recalcule MIENTRAS se escribe — se ve cerrar la
// diferencia, que es la razón de estar editando.
//
// Y para lo que un Excel de verdad hace mejor —buscar y reemplazar en 158
// descripciones, ordenar por importe hasta dar con el duplicado, sumar una
// columna a mano contra el PDF— está la hoja: se baja, se trabaja en Excel y se
// vuelve a subir. Lo que no se hace es meter una hoja de cálculo entera dentro
// del navegador para reimplementar peor lo que contabilidad ya tiene abierto.
// ===========================================================================

/** Las columnas editables, en el orden en que se pegan desde el portapapeles. */
const COLUMNAS = [
  { clave: "fechaOper", titulo: "Fecha", ancho: "w-[104px]" },
  { clave: "descripcion", titulo: "Descripción", ancho: "" },
  { clave: "nroOperacion", titulo: "N.º operación", ancho: "w-[116px]" },
  { clave: "abono", titulo: "Entró", ancho: "w-[110px]", dinero: true },
  { clave: "cargo", titulo: "Salió", ancho: "w-[110px]", dinero: true },
  // El BBVA cobra el impuesto en la misma línea del movimiento y el saldo que
  // imprime ya lo lleva descontado. En su columna, no como fila aparte.
  { clave: "itf", titulo: "ITF", ancho: "w-[92px]", dinero: true },
  { clave: "saldo", titulo: "Saldo", ancho: "w-[120px]", dinero: true },
] as const;

type ClaveColumna = (typeof COLUMNAS)[number]["clave"];

/** `1,234.50` → 123450. Vacío es `null`, que no es lo mismo que cero. */
function aCentimos(texto: string): number | null {
  const limpio = texto
    .trim()
    .replace(/[^\d.,-]/g, "")
    .replace(/,/g, "");
  if (!limpio) return null;
  const n = Number.parseFloat(limpio);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

/** 123450 → `1,234.50`, y `null` → cadena vacía para poder escribir encima. */
function aTexto(centimos: number | null): string {
  if (centimos === null) return "";
  return (centimos / 100).toFixed(2);
}

/** Dónde está el cursor de la rejilla: fila y columna, como en una hoja. */
type Celda = { f: number; c: number };

export function HojaDelExtracto({
  borradorId,
  iniciales,
  yaTocadas,
  declaradoAbonos,
  declaradoCargos,
  abiertaDeEntrada,
  alGuardar,
  alLlegarHoja,
}: {
  borradorId: string;
  iniciales: FilaExtracto[];
  yaTocadas: number[];
  declaradoAbonos: number | null;
  declaradoCargos: number | null;
  /** Se acaba de subir: la hoja se abre sin que haya que pedirla. */
  abiertaDeEntrada: boolean;
  alGuardar: () => void;
  /** Llegó una hoja de Excel: hay que rehacer la rejilla con lo que trae. */
  alLlegarHoja: () => void;
}) {
  const [abierta, setAbierta] = useState(abiertaDeEntrada);

  // La señal de «esto se acaba de subir» llega DESPUÉS del primer pintado: la
  // tarjeta aparece en cuanto la consulta trae el borrador, y solo entonces se
  // sabe que su id es nuevo. Con el valor inicial del `useState` no alcanza —la
  // hoja se quedaba plegada— así que se abre cuando la señal cambia.
  useEffect(() => {
    if (abiertaDeEntrada) setAbierta(true);
  }, [abiertaDeEntrada]);
  const [filas, setFilas] = useState<FilaExtracto[]>(iniciales);
  const [tocadas, setTocadas] = useState<Set<number>>(new Set(yaTocadas));
  const guardarFilas = api.guardarFilas.useMutation();
  const [subiendoHoja, setSubiendoHoja] = useState(false);
  const entradaHoja = useRef<HTMLInputElement>(null);

  // El cursor de la rejilla. `ancla` es donde empezó la selección y `foco`
  // dónde está ahora: entre las dos sale el rectángulo que se copia, igual que
  // en una hoja de cálculo.
  const [ancla, setAncla] = useState<Celda | null>(null);
  const [foco, setFoco] = useState<Celda | null>(null);
  const celdas = useRef(new Map<string, HTMLInputElement>());

  const cambiar = (i: number, clave: ClaveColumna, valor: string) => {
    setFilas((antes) =>
      antes.map((f, j) => {
        if (j !== i) return f;
        if (
          clave === "abono" ||
          clave === "cargo" ||
          clave === "itf" ||
          clave === "saldo"
        ) {
          return { ...f, [clave]: aCentimos(valor) };
        }
        return { ...f, [clave]: valor === "" ? null : valor };
      }),
    );
    setTocadas((antes) => new Set(antes).add(i));
  };

  /**
   * Pegar un bloque desde el portapapeles, que es la mitad de «tipo Excel».
   *
   * Se reparte desde la celda donde se pegó: hacia abajo por filas y hacia la
   * derecha por columnas, exactamente como lo haría una hoja de cálculo. Lo que
   * se salga de la tabla se descarta en vez de crear filas: un extracto tiene
   * las líneas que tiene, y añadir una que el banco no imprimió es inventar un
   * movimiento.
   */
  const pegar = (
    e: React.ClipboardEvent,
    desdeFila: number,
    desdeColumna: number,
  ) => {
    const texto = e.clipboardData.getData("text/plain");
    if (!texto.includes("\t") && !texto.includes("\n")) return; // una celda: normal
    e.preventDefault();

    const bloque = texto
      .replace(/\r/g, "")
      .replace(/\n$/, "")
      .split("\n")
      .map((l) => l.split("\t"));

    const nuevas = [...filas];
    const nuevasTocadas = new Set(tocadas);
    bloque.forEach((linea, df) => {
      const i = desdeFila + df;
      if (i >= nuevas.length) return;
      const fila = nuevas[i];
      if (!fila) return;
      let cambiada = { ...fila };
      linea.forEach((celda, dc) => {
        const col = COLUMNAS[desdeColumna + dc];
        if (!col) return;
        cambiada =
          col.clave === "abono" ||
          col.clave === "cargo" ||
          col.clave === "itf" ||
          col.clave === "saldo"
            ? { ...cambiada, [col.clave]: aCentimos(celda) }
            : { ...cambiada, [col.clave]: celda === "" ? null : celda };
      });
      nuevas[i] = cambiada;
      nuevasTocadas.add(i);
    });
    setFilas(nuevas);
    setTocadas(nuevasTocadas);
  };

  // -------------------------------------------------------------------------
  // Moverse por la rejilla sin soltar el teclado.
  //
  // Es lo que separaba esto de una hoja de cálculo: con 158 líneas por seis
  // columnas, llegar a la celda con el ratón cada vez convierte una revisión de
  // diez minutos en una de cuarenta. Las flechas mueven, Enter baja, el
  // tabulador va a la derecha y salta de fila al final, y Escape suelta.
  //
  // Izquierda y derecha SOLO saltan de celda con el cursor pegado al borde del
  // texto. Dentro de una descripción de sesenta caracteres, la flecha tiene que
  // seguir moviendo el cursor por las letras: es lo que hace cualquier campo de
  // texto, y romperlo es peor que no tener navegación.
  // -------------------------------------------------------------------------
  const clave = (f: number, c: number) => `${f}:${c}`;

  const irA = (f: number, c: number, extender: boolean) => {
    const nf = Math.min(Math.max(f, 0), filas.length - 1);
    const nc = Math.min(Math.max(c, 0), COLUMNAS.length - 1);
    const el = celdas.current.get(clave(nf, nc));
    if (!el) return;
    el.focus();
    // Al llegar con el teclado se selecciona todo, como en Excel: lo siguiente
    // que se escriba reemplaza. Al llegar con el ratón no, porque ahí lo que
    // se quiere es poner el cursor donde se pulsó.
    el.select();
    setFoco({ f: nf, c: nc });
    if (!extender) setAncla({ f: nf, c: nc });
  };

  const teclado = (
    e: React.KeyboardEvent<HTMLInputElement>,
    f: number,
    c: number,
  ) => {
    const el = e.currentTarget;
    const sinSeleccion = el.selectionStart === el.selectionEnd;
    const alPrincipio = sinSeleccion && el.selectionStart === 0;
    const alFinal = sinSeleccion && el.selectionStart === el.value.length;

    if (e.key === "Escape") return el.blur();
    if (e.key === "ArrowDown" || (e.key === "Enter" && !e.shiftKey)) {
      e.preventDefault();
      return irA(f + 1, c, e.shiftKey && e.key !== "Enter");
    }
    if (e.key === "ArrowUp" || (e.key === "Enter" && e.shiftKey)) {
      e.preventDefault();
      return irA(f - 1, c, false);
    }
    if (e.key === "Tab") {
      e.preventDefault();
      const siguiente = e.shiftKey ? c - 1 : c + 1;
      if (siguiente < 0) return irA(f - 1, COLUMNAS.length - 1, false);
      if (siguiente >= COLUMNAS.length) return irA(f + 1, 0, false);
      return irA(f, siguiente, false);
    }
    if (e.key === "ArrowLeft" && alPrincipio && !e.shiftKey) {
      e.preventDefault();
      return irA(f, c - 1, false);
    }
    if (e.key === "ArrowRight" && alFinal && !e.shiftKey) {
      e.preventDefault();
      return irA(f, c + 1, false);
    }
  };

  /**
   * Copiar un rectángulo de celdas como lo haría Excel.
   *
   * El rectángulo se marca pulsando una celda y otra con Mayúsculas, que es el
   * gesto de siempre. Con una sola celda seleccionada no se interviene: ahí lo
   * que se quiere copiar es el texto de dentro, no la celda entera.
   */
  const copiar = (e: React.ClipboardEvent) => {
    if (!ancla || !foco) return;
    const f1 = Math.min(ancla.f, foco.f);
    const f2 = Math.max(ancla.f, foco.f);
    const c1 = Math.min(ancla.c, foco.c);
    const c2 = Math.max(ancla.c, foco.c);
    if (f1 === f2 && c1 === c2) return;
    e.preventDefault();
    const bloque: string[] = [];
    for (let i = f1; i <= f2; i++) {
      const fila = filas[i];
      if (!fila) continue;
      const celdasDeLaFila: string[] = [];
      for (let j = c1; j <= c2; j++) {
        const col = COLUMNAS[j];
        if (!col) continue;
        celdasDeLaFila.push(
          "dinero" in col && col.dinero
            ? aTexto(fila[col.clave] as number | null)
            : ((fila[col.clave] as string | null) ?? ""),
        );
      }
      bloque.push(celdasDeLaFila.join("\t"));
    }
    e.clipboardData.setData("text/plain", bloque.join("\n"));
  };

  /** Si la celda cae dentro del rectángulo marcado, para poder pintarla. */
  const enLaSeleccion = (f: number, c: number) =>
    ancla !== null &&
    foco !== null &&
    (ancla.f !== foco.f || ancla.c !== foco.c) &&
    f >= Math.min(ancla.f, foco.f) &&
    f <= Math.max(ancla.f, foco.f) &&
    c >= Math.min(ancla.c, foco.c) &&
    c <= Math.max(ancla.c, foco.c);

  /**
   * La hoja corregida en Excel, de vuelta.
   *
   * No pasa por `guardarFilas` del contrato tipado porque lo que sube es un
   * archivo, no datos; el servidor lo abre y escribe POR ESE mismo camino, así
   * que la validación y el recálculo del cuadre son los de siempre.
   */
  const subirHoja = async (archivoHoja: File) => {
    setSubiendoHoja(true);
    const cuerpo = new FormData();
    cuerpo.append("archivo", archivoHoja);
    try {
      const r = await fetch(`/hoja/extracto/${borradorId}`, {
        method: "POST",
        body: cuerpo,
        credentials: "include",
      });
      const cuerpoRespuesta = (await r.json()) as {
        message?: string;
        dice?: string;
        leidas?: number;
        cambiadas?: number;
        anadidas?: number;
        quitadas?: number;
        rechazadas?: Array<{ fila: number; motivo: string }>;
      };
      if (!r.ok) {
        toast.error(cuerpoRespuesta.message ?? "No se pudo leer esa hoja");
        return;
      }
      // Qué entró, en cifras. «Guardado» a secas obligaría a volver a comparar
      // las 158 líneas contra el Excel para saber qué se aplicó.
      const partes = [
        `${cuerpoRespuesta.leidas ?? 0} líneas`,
        `${cuerpoRespuesta.cambiadas ?? 0} cambiadas`,
      ];
      if (cuerpoRespuesta.anadidas)
        partes.push(`${cuerpoRespuesta.anadidas} añadidas`);
      if (cuerpoRespuesta.quitadas)
        partes.push(`${cuerpoRespuesta.quitadas} quitadas`);
      toast.success(partes.join(" · "), {
        description: cuerpoRespuesta.dice,
      });
      // Las filas que la hoja traía mal van aparte y con su número: se
      // quedaron como estaban, y quien las escribió tiene que saber cuáles.
      if (cuerpoRespuesta.rechazadas?.length) {
        toast.warning(
          `${cuerpoRespuesta.rechazadas.length} fila(s) de la hoja quedaron sin aplicar`,
          {
            description: cuerpoRespuesta.rechazadas
              .slice(0, 3)
              .map((x) => `fila ${x.fila}: ${x.motivo}`)
              .join(" · "),
            duration: 12_000,
          },
        );
      }
      alLlegarHoja();
    } catch {
      toast.error("No se pudo subir la hoja");
    } finally {
      setSubiendoHoja(false);
    }
  };

  // El cuadre en vivo. Se calcula aquí para poder verlo mientras se escribe,
  // pero el que vale es el del servidor: una comprobación que solo vive en el
  // navegador se salta con curl, y `guardarFilas` la rehace al guardar.
  const entro = filas.reduce((s, f) => s + (f.abono ?? 0), 0);
  // El ITF suma en el total aunque en la fila vaya en su columna: es dinero que
  // salió de la cuenta igual que cualquier otro cargo. Es la misma cuenta que
  // hace el servidor; si difirieran, la rejilla diría una cosa y el resumen del
  // mes otra sobre las mismas líneas.
  const salio = filas.reduce((s, f) => s + (f.cargo ?? 0) + (f.itf ?? 0), 0);
  const cuadraAbonos = declaradoAbonos === null || declaradoAbonos === entro;
  const cuadraCargos = declaradoCargos === null || declaradoCargos === salio;

  // EL SALDO CALCULADO, fila por fila, y dónde se separa del impreso.
  //
  // Contabilidad lo pidió así: «una columna que ya la sé calculada, que vaya
  // sumando fila por fila, y yo ver también esa comprobación». Es la misma
  // cuenta que hace la validación del servidor, pero a la vista y mientras se
  // escribe — y es lo que convierte «este extracto no cuadra» en «se separa en
  // la línea 26, y por 23.730,00».
  //
  // Arranca del saldo de apertura que IMPLICA la primera línea: su saldo
  // impreso menos lo que ella misma movió. Así la primera calculada coincide
  // con la primera impresa y lo que se compara de ahí en adelante es la cadena,
  // no el punto de partida.
  const mueve = (f: FilaExtracto) =>
    (f.abono ?? 0) - (f.cargo ?? 0) - (f.itf ?? 0);
  const calculados: Array<number | null> = [];
  {
    const primera = filas[0];
    let acumulado =
      primera && primera.saldo !== null ? primera.saldo - mueve(primera) : null;
    for (const f of filas) {
      if (acumulado === null) {
        calculados.push(null);
        continue;
      }
      acumulado += mueve(f);
      calculados.push(acumulado);
    }
  }

  // Dónde se separa del que imprime el banco. Es la comprobación que dice si
  // falta un movimiento, y verla moverse mientras se corrige es lo que hace
  // útil editar aquí.
  const roturas: number[] = [];
  for (let i = 0; i < filas.length; i++) {
    const f = filas[i];
    const c = calculados[i];
    if (!f || f.saldo === null || c === null || c === undefined) continue;
    if (c !== f.saldo) roturas.push(i);
  }

  const hayCambios = tocadas.size > 0;

  const guardar = () =>
    guardarFilas.mutate(
      { params: { id: borradorId }, body: { filas, tocadas: [...tocadas] } },
      {
        onSuccess: (r: {
          status: number;
          body: { dice?: string; message?: string };
        }) => {
          if (r.status !== 200) {
            toast.error(r.body.message ?? "No se pudo guardar");
            return;
          }
          toast.success(r.body.dice ?? "Guardado");
          alGuardar();
        },
        onError: () => toast.error("No se pudo guardar"),
      },
    );

  /**
   * La ida y la vuelta en Excel.
   *
   * Va en una variable porque se pinta EN LOS DOS ESTADOS: plegada y abierta.
   * Estaba solo dentro de la rejilla abierta, y como la rejilla llegaba plegada
   * detrás de una línea que parecía un separador, los dos botones no se veían
   * nunca — la mitad del trabajo, invisible.
   *
   * Lo que se baja es lo que hay GUARDADO en el servidor, no lo escrito en la
   * rejilla, así que con cambios sin guardar el botón no deja: bajarse la hoja
   * y encontrarla sin las tres correcciones que uno acaba de escribir es la
   * forma más rápida de perder trabajo.
   */
  const barraDeExcel = (borde: "border-t" | "border-b") => (
    <div className={cn("flex flex-wrap items-center gap-2 px-3 py-2", borde)}>
      <Button
        size="sm"
        variant="outline"
        asChild={!hayCambios}
        disabled={hayCambios}
      >
        {hayCambios ? (
          <span>
            <Download className="mr-1.5 size-3.5" />
            Bajar a Excel
          </span>
        ) : (
          <a href={`/hoja/extracto/${borradorId}`} download>
            <Download className="mr-1.5 size-3.5" />
            Bajar a Excel
          </a>
        )}
      </Button>
      <Button
        size="sm"
        variant="outline"
        disabled={subiendoHoja}
        onClick={() => entradaHoja.current?.click()}
      >
        <FileUp className="mr-1.5 size-3.5" />
        {subiendoHoja ? "Leyendo la hoja…" : "Subir la hoja corregida"}
      </Button>
      <input
        ref={entradaHoja}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void subirHoja(f);
          e.target.value = "";
        }}
      />
      <span className="text-[11px] text-muted-foreground">
        {hayCambios
          ? "Guardá los cambios antes de bajarte la hoja: se baja lo guardado."
          : "Se baja, se trabaja en Excel y se vuelve a subir. Manda el orden de las filas, no la columna #."}
      </span>
    </div>
  );

  // Plegada. Un extracto que ya se revisó no tiene por qué ocupar la pantalla
  // con sus 158 líneas, pero abrirlo tiene que parecer un botón: antes era una
  // línea gris punteada a ancho completo, que se lee como un separador.
  if (!abierta) {
    return (
      <div className="mt-3 rounded border">
        <div className="flex flex-wrap items-center gap-2 px-3 py-2">
          <Button size="sm" onClick={() => setAbierta(true)}>
            <Pencil className="mr-1.5 size-3.5" />
            Revisar y corregir las {filas.length.toLocaleString("es-PE")} líneas
          </Button>
          {yaTocadas.length > 0 ? (
            <span className="text-[11.5px] text-amber-700 dark:text-amber-500">
              {yaTocadas.length} corregida{yaTocadas.length === 1 ? "" : "s"} a
              mano
            </span>
          ) : null}
          <span
            className={cn(
              "ml-auto text-[11.5px]",
              roturas.length
                ? "text-red-600 dark:text-red-500"
                : "text-emerald-700 dark:text-emerald-500",
            )}
          >
            {roturas.length
              ? `la cadena de saldos se rompe en ${roturas.length} línea${roturas.length === 1 ? "" : "s"}`
              : "la cadena de saldos cierra"}
          </span>
        </div>
        {/* El Excel, también plegada: es el otro camino para corregir, y
            esconderlo detrás de «abrir la rejilla» lo hace invisible. */}
        {barraDeExcel("border-t")}
      </div>
    );
  }

  return (
    <div className="mt-3 rounded border">
      <div className="flex flex-wrap items-center gap-3 border-b bg-muted/30 px-3 py-2">
        <span className="font-medium text-[12px]">
          {filas.length.toLocaleString("es-PE")} líneas
        </span>
        <span className="text-[11.5px] tabular-nums">
          <span className="text-muted-foreground">Entró </span>
          <b className={cuadraAbonos ? "" : "text-red-600 dark:text-red-500"}>
            {dinero(entro)}
          </b>
          {declaradoAbonos !== null ? (
            <span className="text-muted-foreground">
              {" "}
              / {dinero(declaradoAbonos)}
            </span>
          ) : null}
        </span>
        <span className="text-[11.5px] tabular-nums">
          <span className="text-muted-foreground">Salió </span>
          <b className={cuadraCargos ? "" : "text-red-600 dark:text-red-500"}>
            {dinero(salio)}
          </b>
          {declaradoCargos !== null ? (
            <span className="text-muted-foreground">
              {" "}
              / {dinero(declaradoCargos)}
            </span>
          ) : null}
        </span>
        <span
          className={cn(
            "text-[11.5px]",
            roturas.length
              ? "text-red-600 dark:text-red-500"
              : "text-emerald-700 dark:text-emerald-500",
          )}
        >
          {roturas.length
            ? `la cadena de saldos se rompe en ${roturas.length} línea${roturas.length === 1 ? "" : "s"}`
            : "la cadena de saldos cierra"}
        </span>

        <div className="ml-auto flex items-center gap-2">
          {hayCambios ? (
            <span className="text-[11.5px] text-amber-700 dark:text-amber-500">
              {tocadas.size} sin guardar
            </span>
          ) : null}
          <Button
            size="sm"
            disabled={!hayCambios || guardarFilas.isPending}
            onClick={guardar}
          >
            {guardarFilas.isPending ? "Guardando…" : "Guardar los cambios"}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setAbierta(false)}>
            Cerrar
          </Button>
        </div>
      </div>

      {barraDeExcel("border-b")}

      <p className="border-b px-3 py-1.5 text-[11px] text-muted-foreground">
        Se escribe directo en la celda y las flechas mueven por la rejilla:
        Enter baja, el tabulador va a la derecha. Se puede pegar un bloque desde
        Excel —se reparte desde donde se pegue— y copiar otro marcándolo con
        Mayúsculas. Nada de esto entra a la base hasta que se confirme el
        archivo.
      </p>

      {/* Contenedor propio con scroll y `min-h-0`: sin eso la tabla crece por
          debajo del recorte de la tarjeta y las últimas líneas quedan fuera de
          alcance, que es el fallo de #37 en otra pantalla.

          El `onCopy` va aquí y no en cada celda: el evento de copiar sube desde
          el input que tiene el foco, y engancharlo arriba deja un solo sitio
          donde decidir si se copia el rectángulo o el texto de la celda. */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: el copiado sube de los inputs */}
      <div className="max-h-[420px] min-h-0 overflow-auto" onCopy={copiar}>
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 z-10 bg-background">
            <tr className="border-b text-[10.5px] text-muted-foreground uppercase tracking-wider">
              <th className="w-[44px] px-2 py-1.5 text-right font-semibold">
                #
              </th>
              {COLUMNAS.map((c) => (
                <th
                  key={c.clave}
                  className={cn(
                    "px-1 py-1.5 font-semibold",
                    c.ancho,
                    "dinero" in c && c.dinero ? "text-right" : "text-left",
                  )}
                >
                  {c.titulo}
                </th>
              ))}
              {/* No se escribe: sale de sumar las líneas. Está para
                  comprobarla contra la que imprime el banco. */}
              <th
                className="w-[124px] px-1 py-1.5 text-right font-semibold"
                title="Saldo que sale de sumar las líneas, una a una"
              >
                Saldo calc.
              </th>
            </tr>
          </thead>
          <tbody>
            {filas.map((f, i) => (
              <tr
                key={`${f.pagina}-${f.linea}-${i}`}
                className={cn(
                  "border-b last:border-0",
                  tocadas.has(i) && "bg-amber-500/5",
                  roturas.includes(i) && "bg-red-600/5",
                )}
              >
                <td className="px-2 py-0.5 text-right text-[11px] text-muted-foreground tabular-nums">
                  {i + 1}
                </td>
                {COLUMNAS.map((c, ci) => {
                  const esDinero = "dinero" in c && c.dinero;
                  const valor = esDinero
                    ? aTexto(f[c.clave] as number | null)
                    : ((f[c.clave] as string | null) ?? "");
                  return (
                    <td
                      key={c.clave}
                      className={cn(
                        "px-0.5 py-0.5",
                        enLaSeleccion(i, ci) && "bg-cyan-500/15",
                      )}
                    >
                      <input
                        ref={(el) => {
                          // El mapa de celdas es lo que permite que las flechas
                          // sepan a qué input saltar. Se limpia al desmontar,
                          // o la rejilla de un extracto guardaría referencias
                          // muertas a la del anterior.
                          if (el) celdas.current.set(clave(i, ci), el);
                          else celdas.current.delete(clave(i, ci));
                        }}
                        value={valor}
                        onChange={(e) => cambiar(i, c.clave, e.target.value)}
                        onPaste={(e) => pegar(e, i, ci)}
                        onKeyDown={(e) => teclado(e, i, ci)}
                        // Con Mayúsculas se extiende el rectángulo desde donde
                        // se empezó; sin ellas, la celda pulsada es el nuevo
                        // punto de partida. Es el gesto de cualquier hoja.
                        onMouseDown={(e) => {
                          if (e.shiftKey) {
                            e.preventDefault();
                            setFoco({ f: i, c: ci });
                          }
                        }}
                        onFocus={(e) => {
                          setFoco({ f: i, c: ci });
                          if (!e.relatedTarget || !ancla)
                            setAncla({ f: i, c: ci });
                        }}
                        onClick={() => setAncla({ f: i, c: ci })}
                        className={cn(
                          "w-full rounded-sm border border-transparent bg-transparent px-1.5 py-1 focus:border-cyan-500 focus:bg-background focus:outline-none",
                          esDinero && "text-right tabular-nums",
                        )}
                      />
                    </td>
                  );
                })}
                {/* El calculado, y en rojo cuando se separa del impreso: ahí
                    es donde el lector se saltó algo o donde una corrección
                    todavía no cierra. */}
                <td
                  className={cn(
                    "px-2 py-0.5 text-right tabular-nums",
                    roturas.includes(i)
                      ? "font-semibold text-red-700 dark:text-red-500"
                      : "text-muted-foreground",
                  )}
                >
                  {dinero(calculados[i] ?? null)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Campo({
  titulo,
  children,
}: {
  titulo: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <div className="font-semibold text-[10.5px] text-muted-foreground uppercase tracking-wider">
        {titulo}
      </div>
      {children}
    </div>
  );
}
